import { z } from 'zod';

export const JobStatusSchema = z.enum([
  'running',
  'awaiting_input',
  'completed',
  'failed',
  'cancelled',
]);
export type JobStatus = z.infer<typeof JobStatusSchema>;

// ─── Jobs page (Yours + Suggested) response ─────────────────────────────
const JobsPageYoursItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  scope: z.enum(['system', 'tenant']),
  featured: z.boolean(),
  favorited: z.boolean(),
  run_count: z.number(),
  last_run_at: z.string().nullable(),
  last_status: JobStatusSchema.nullable(),
});
export type JobsPageYoursItem = z.infer<typeof JobsPageYoursItemSchema>;

const JobsPageSuggestedItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  featured: z.boolean(),
  defaultPositionIds: z.array(z.string()),
  defaultUnitIds: z.array(z.string()),
});
export type JobsPageSuggestedItem = z.infer<typeof JobsPageSuggestedItemSchema>;

export const JobsPageResponseSchema = z.object({
  yours: z.array(JobsPageYoursItemSchema),
  suggested: z.array(JobsPageSuggestedItemSchema),
  favorited_ids: z.array(z.string()),
  dismissed_count: z.number(),
});
export type JobsPageResponse = z.infer<typeof JobsPageResponseSchema>;

export const JobListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: JobStatusSchema,
  assigned_member: z.object({ id: z.string(), display_name: z.string() }),
  created_at: z.string(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  acknowledged_at: z.string().nullable(),
});
export type JobListItem = z.infer<typeof JobListItemSchema>;

export const ArtifactSchema = z.object({
  id: z.string(),
  turn_number: z.number(),
  parameter_name: z.string().nullable(),
  mime_type: z.string(),
  size_bytes: z.number(),
  content: z.string(),
  created_at: z.string(),
});
export type Artifact = z.infer<typeof ArtifactSchema>;

export const JobAttachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  mime_type: z.string(),
  size_bytes: z.number().int(),
  sha256: z.string(),
  createdAt: z.string(),
});
export type JobAttachment = z.infer<typeof JobAttachmentSchema>;

// ─── Chat stream / job_messages ────────────────────────────────────────
// Provider-agnostic content blocks (mirrors Anthropic's shape stored by the
// runner — see docs/architecture/data-model.md §5.1).
export const JobMessageBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string() }),
  z.object({
    type: z.literal('tool_use'),
    id: z.string(),
    name: z.string(),
    input: z.unknown(),
  }),
  z.object({
    type: z.literal('tool_result'),
    tool_use_id: z.string(),
    content: z.union([z.string(), z.array(z.unknown())]),
    is_error: z.boolean().optional(),
  }),
]);
export type JobMessageBlock = z.infer<typeof JobMessageBlockSchema>;

export const JobMessageSchema = z.object({
  id: z.string(),
  turn_number: z.number().int(),
  role: z.enum(['user', 'assistant']),
  content: z.array(JobMessageBlockSchema),
  created_by_tenant_user_id: z.string().nullable(),
  created_at: z.string(),
});
export type JobMessage = z.infer<typeof JobMessageSchema>;

// ─── request_input payload (Aria's pending question) ──────────────────
export const PendingInputFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(['text', 'long_text', 'choice', 'number', 'file']),
  choices: z.array(z.string()).optional(),
  required: z.boolean().optional(),
});
export type PendingInputField = z.infer<typeof PendingInputFieldSchema>;

export const PendingInputRequestSchema = z.object({
  prompt: z.string(),
  fields: z.array(PendingInputFieldSchema),
});
export type PendingInputRequest = z.infer<typeof PendingInputRequestSchema>;

export const JobDetailSchema = z.object({
  id: z.string(),
  title: z.string(),
  instructions: z.string(),
  inputs_description: z.string().nullable(),
  outputs_description: z.string().nullable(),
  input_schema: z.unknown().nullable(),
  output_schema: z.unknown().nullable(),
  inputs: z.record(z.unknown()),
  assigned_member: z.object({ id: z.string(), display_name: z.string() }),
  created_by_tenant_user: z.object({ id: z.string(), display_name: z.string() }),
  status: JobStatusSchema,
  error_message: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  cancelled_at: z.string().nullable(),
  paused_at: z.string().nullable(),
  pending_input_request: PendingInputRequestSchema.nullable(),
  acknowledged_at: z.string().nullable(),
  created_from_template_id: z.string().nullable(),
  created_at: z.string(),
  remember_outputs: z.boolean(),
  artifacts: z.array(ArtifactSchema),
  attachments: z.array(JobAttachmentSchema),
  messages: z.array(JobMessageSchema),
  user_names: z.record(z.string()).optional(),
  last_seen_turn_number: z.number().int().nullable(),
});
export type JobDetail = z.infer<typeof JobDetailSchema>;

// ─── Lifecycle route bodies ────────────────────────────────────────────
export const PostJobMessageBodySchema = z.object({
  text: z.string().min(1).max(8000),
});
export type PostJobMessageBody = z.infer<typeof PostJobMessageBodySchema>;

export const ResumeJobBodySchema = z.object({
  text: z.string().min(1).max(8000).optional(),
});
export type ResumeJobBody = z.infer<typeof ResumeJobBodySchema>;

export const AnswerJobInputBodySchema = z.union([
  z.object({ kind: z.literal('freeform'), text: z.string().min(1).max(8000) }),
  z.object({ kind: z.literal('fields'), values: z.record(z.unknown()) }),
]);
export type AnswerJobInputBody = z.infer<typeof AnswerJobInputBodySchema>;

export const JobsListResponseSchema = z.object({
  items: z.array(JobListItemSchema),
  total: z.number(),
});
export type JobsListResponse = z.infer<typeof JobsListResponseSchema>;

export const JobDetailResponseSchema = z.object({ item: JobDetailSchema });
export type JobDetailResponse = z.infer<typeof JobDetailResponseSchema>;

// Caller picks exactly one of assigned_member_id (existing Member) OR
// staff_profile_id (implicit hire-on-submit via the recommendation panel).
// Server enforces "exactly one"; the zod type stays a loose object so call
// sites can build the body either way.
export const CreateJobBodySchema = z.object({
  job_template_id: z.string().optional(),
  assigned_member_id: z.string().optional(),
  staff_profile_id: z.string().optional(),
  title: z.string().trim().min(1).max(200).optional(),
  instructions: z.string().optional(),
  // Override for this run only. Null clears the template's value; absent
  // means "use the template's outputs_description as-is."
  outputs_description: z.string().nullable().optional(),
  inputs: z.record(z.unknown()).default({}),
  remember_outputs: z.boolean().optional(),
  attachment_ids: z.array(z.string()).max(10).optional(),
});
export type CreateJobBody = z.infer<typeof CreateJobBodySchema>;

/* ─── Recommendations ──────────────────────────────────────────────── */

const PositionRefSchema = z.object({ id: z.string(), name: z.string() });
const UnitRefSchema = z.object({ id: z.string(), name: z.string() });

export const RecommendedMemberSchema = z.object({
  kind: z.literal('member'),
  id: z.string(),
  first_name: z.string(),
  last_name: z.string().nullable(),
  positions: z.array(PositionRefSchema),
  units: z.array(UnitRefSchema),
  score: z.number(),
});
export type RecommendedMember = z.infer<typeof RecommendedMemberSchema>;

export const RecommendedStaffProfileSchema = z.object({
  kind: z.literal('staff_profile'),
  id: z.string(),
  first_name: z.string(),
  last_name: z.string().nullable(),
  description: z.string().nullable(),
  positions: z.array(PositionRefSchema),
  units: z.array(UnitRefSchema),
  score: z.number(),
});
export type RecommendedStaffProfile = z.infer<typeof RecommendedStaffProfileSchema>;

export const JobRecommendationsResponseSchema = z.object({
  added: z.array(RecommendedMemberSchema),
  availableToAdd: z.array(RecommendedStaffProfileSchema),
});
export type JobRecommendationsResponse = z.infer<typeof JobRecommendationsResponseSchema>;

export const UpdateJobBodySchema = z.object({
  remember_outputs: z.boolean().optional(),
});
export type UpdateJobBody = z.infer<typeof UpdateJobBodySchema>;
